import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { videos } from "@/mocks/videos";

const slides = videos.filter((item) => item.featured);

export default function BannerCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const go = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-background-100"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="热门影视焦点轮播"
    >
      <div className="relative h-[440px] w-full md:h-[560px]">
        {slides.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.banner || item.cover}
              alt={`${item.title}焦点海报`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1440px] flex-col justify-end px-4 pb-16 md:px-6 md:pb-20 lg:px-10">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-semibold text-primary-300">
              <i className="ri-fire-fill text-sm"></i>
              本周热播 · {slides[index].type}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
              {slides[index].title}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
              {slides[index].tagline}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
              <span className="font-label text-primary-400">
                <i className="ri-star-fill mr-1"></i>
                {slides[index].rating.toFixed(1)}
              </span>
              <span>{slides[index].year}</span>
              <span>{slides[index].region}</span>
              <span>{slides[index].status}</span>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slides[index].id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg"></i>
                立即查看
              </Link>
              <Link
                to={`/category?type=${encodeURIComponent(slides[index].type)}`}
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                更多同类
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-300 bg-background-50/70 text-foreground-800 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400 md:flex lg:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-300 bg-background-50/70 text-foreground-800 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400 md:flex lg:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {slides.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到第${i + 1}张`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-7 bg-primary-500" : "w-1.5 bg-background-500"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}